

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Typography,
  Switch,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Divider
} from '@mui/material';
import { 
  Add, 
  Delete, 
  Edit,
  Search as SearchIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  LocalShipping,
  DirectionsCar,
  TwoWheeler
} from '@mui/icons-material';
import AddDeliveryAgent from "./AddDeliveryAgent";
import EditDeliveryAgent from "./EditDeliveryAgent";
import CustomDialog from "../../CustomDialog";
import { deliveryAgentAPI } from "./deliveryAgentAPI";

const DeliveryAgentsManagement = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    agentId: null,
    agentName: ''
  });
  const [notification, setNotification] = useState({
    open: false,
    type: '',
    message: ''
  });

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await deliveryAgentAPI.getAll();
      if (response.data.success) {
        setAgents(response.data.data);
      }
    } catch (error) {
      showNotification('Error fetching agents', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (agentId) => {
    try {
      const response = await deliveryAgentAPI.toggleStatus(agentId);
      if (response.data.success) {
        fetchAgents();
        showNotification(response.data.message, 'success');
      }
    } catch (error) {
      showNotification('Error updating agent status', 'error');
    }
  };

  const handleEditClick = (agent) => {
    setSelectedAgent(agent);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (agent) => {
    setDeleteDialog({
      open: true,
      agentId: agent.agentId,
      agentName: agent.name
    });
  };

  const handleDelete = async () => {
    try {
      const response = await deliveryAgentAPI.delete(deleteDialog.agentId);
      if (response.data.success) {
        fetchAgents();
        setDeleteDialog({ open: false, agentId: null, agentName: '' });
        showNotification('Agent deleted successfully', 'success');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error deleting agent';
      showNotification(errorMessage, 'error');
      setDeleteDialog({ open: false, agentId: null, agentName: '' });
    }
  };

  const showNotification = (message, type = 'info') => {
    setNotification({ open: true, message, type });
  };

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const filteredAgents = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return agents;
    }
    const query = debouncedSearchQuery.toLowerCase();
    return agents.filter(agent =>
      agent.agentId.toLowerCase().includes(query) ||
      agent.employeeId.toLowerCase().includes(query) ||
      agent.name.toLowerCase().includes(query) ||
      agent.phone.includes(query) ||
      agent.vehicleNumber.toLowerCase().includes(query)
    );
  }, [agents, debouncedSearchQuery]);

  const getVehicleIcon = (vehicleType) => {
    switch(vehicleType.toLowerCase()) {
      case 'bike':
      case 'motorcycle':
        return <TwoWheeler />;
      case 'car':
        return <DirectionsCar />;
      case 'van':
      case 'truck':
        return <LocalShipping />;
      default:
        return <DirectionsCar />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#006D77' }} />
      </Box>
    );
  }

  const CardView = () => (
    <Grid container spacing={3}>
      {filteredAgents.length === 0 ? (
        <Grid item xs={12}>
          <Paper sx={{ p: 8, textAlign: 'center' }}>
            <LocalShipping sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No delivery agents found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'Try adjusting your search' : 'Add your first delivery agent to get started'}
            </Typography>
          </Paper>
        </Grid>
      ) : (
        filteredAgents.map((agent) => (
          <Grid item xs={12} md={6} lg={4} key={agent.id}>
            <Card sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-4px)'
              }
            }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ color: '#006D77' }}>
                      {agent.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Agent ID: {agent.agentId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Employee ID: {agent.employeeId}
                    </Typography>
                  </Box>
                  <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
                    <Chip
                      label={agent.isActive ? 'Active' : 'Inactive'}
                      color={agent.isActive ? 'success' : 'default'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      label={agent.agentRole === 'delivery' ? 'Delivery' : 'Sample Collection'}
                      size="small"
                      sx={{ 
                        bgcolor: agent.agentRole === 'delivery' ? '#DBEAFE' : '#FEF3C7',
                        color: agent.agentRole === 'delivery' ? '#2563EB' : '#D97706'
                      }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Contact
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {agent.phone}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {agent.email}
                  </Typography>
                </Box>

                <Box sx={{ bgcolor: '#f1f5f9', borderRadius: 2, p: 2, mb: 2 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {getVehicleIcon(agent.vehicleType)}
                    <Typography variant="body2" fontWeight="600">
                      Vehicle Information
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Type: {agent.vehicleType.toUpperCase()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Number: {agent.vehicleNumber}
                  </Typography>
                </Box>

                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Current Orders
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {agent.currentOrders}/{agent.maxOrderCapacity}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Total Deliveries
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {agent.totalDeliveries}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0, justifyContent: 'space-between', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Switch
                    checked={agent.isActive}
                    onChange={() => handleToggleStatus(agent.agentId)}
                    color="success"
                    size="small"
                  />
                  <Typography variant="caption" color="text.secondary">
                    {agent.isActive ? 'Active' : 'Inactive'}
                  </Typography>
                </Box>
                <Box>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditClick(agent)}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(agent)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardActions>
            </Card>
          </Grid>
        ))
      )}
    </Grid>
  );

  const TableView = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead sx={{ bgcolor: '#f8fafc' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Agent ID</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Employee ID</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Vehicle</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Current Orders</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Total Deliveries</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredAgents.length === 0 ? (
            <TableRow>
              <TableCell colSpan={10} align="center">
                <Box py={4}>
                  <LocalShipping sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No delivery agents found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {searchQuery ? 'Try adjusting your search' : 'Add your first delivery agent to get started'}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            filteredAgents.map((agent) => (
              <TableRow key={agent.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold" color="#006D77">
                    {agent.agentId}
                  </Typography>
                </TableCell>
                <TableCell>{agent.employeeId}</TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="600">
                    {agent.name}
                  </Typography>
                </TableCell>
                <TableCell>{agent.phone}</TableCell>
                <TableCell>
                  <Chip
                    label={agent.agentRole === 'delivery' ? 'Delivery' : 'Sample Collection'}
                    size="small"
                    sx={{ 
                      bgcolor: agent.agentRole === 'delivery' ? '#DBEAFE' : '#FEF3C7',
                      color: agent.agentRole === 'delivery' ? '#2563EB' : '#D97706'
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getVehicleIcon(agent.vehicleType)}
                    <Box>
                      <Typography variant="body2" fontWeight="600">
                        {agent.vehicleType.toUpperCase()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {agent.vehicleNumber}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={`${agent.currentOrders} / ${agent.maxOrderCapacity}`}
                    size="small"
                    color={agent.currentOrders < agent.maxOrderCapacity ? 'success' : 'warning'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="600">
                    {agent.totalDeliveries}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Switch
                      checked={agent.isActive}
                      onChange={() => handleToggleStatus(agent.agentId)}
                      color="success"
                      size="small"
                    />
                    <Chip
                      label={agent.isActive ? 'Active' : 'Inactive'}
                      color={agent.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditClick(agent)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(agent)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: 4 }}>
      <Box sx={{ p: 3, ml: { md: '300px' } }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" fontWeight="bold" sx={{ mb: 1, color: '#1e293b' }}>
            Delivery Agents Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage delivery agents and monitor their performance
          </Typography>
        </Box>

        {/* Action Bar */}
        <Paper sx={{ p: 2.5, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
          <TextField
            placeholder="Search agents..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ flexGrow: 1, minWidth: 250, maxWidth: 400 }}
          />
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="cards">
                <Tooltip title="Card View">
                  <GridViewIcon />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="table">
                <Tooltip title="Table View">
                  <ViewListIcon />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setAddDialogOpen(true)}
              sx={{
                bgcolor: '#006D77',
                '&:hover': { bgcolor: '#005c64' },
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Add Delivery Agent
            </Button>
          </Box>
        </Paper>

        {/* View Content */}
        {viewMode === 'cards' ? <CardView /> : <TableView />}

        {/* Add Agent Dialog */}
        <AddDeliveryAgent
          open={addDialogOpen}
          onClose={() => setAddDialogOpen(false)}
          onSuccess={(message, type = 'success') => {
            fetchAgents();
            showNotification(message, type);
          }}
        />

        {/* Edit Agent Dialog */}
        <EditDeliveryAgent
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setSelectedAgent(null);
          }}
          onSuccess={(message, type = 'success') => {
            fetchAgents();
            showNotification(message, type);
          }}
          agent={selectedAgent}
        />

        {/* Delete Confirmation Dialog */}
        <CustomDialog
          open={deleteDialog.open}
          type="warning"
          title="Delete Delivery Agent"
          message={`Are you sure you want to delete ${deleteDialog.agentName}? This action cannot be undone.`}
          buttonText="Delete"
          secondaryButtonText="Cancel"
          onClose={handleDelete}
          onSecondaryAction={() => setDeleteDialog({ open: false, agentId: null, agentName: '' })}
        />

        {/* Notification Dialog */}
        <CustomDialog
          open={notification.open}
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification({ ...notification, open: false })}
        />
      </Box>
    </Box>
  );
};

export default DeliveryAgentsManagement;